/**
 * Color-temperature (Kelvin) → sRGB, via Tanner Helland's widely-used
 * black-body approximation:
 *   https://tannerhelland.com/2012/09/18/convert-temperature-rgb-algorithm-code.html
 *
 * Accurate enough for tinting a light source (not a colorimetric reference).
 * ~6500 K ≈ neutral white (D65 daylight); lower = warm (candle/tungsten),
 * higher = cool (overcast/shade). Input is clamped to the range the fit is
 * valid over, [1000, 40000] K.
 *
 * Pure + allocation-light. Returns channels in 0..1 (three.js Color space).
 */
function clamp255(v) {
  return v < 0 ? 0 : v > 255 ? 255 : v
}

/** Kelvin → { r, g, b } with each channel in 0..1. */
export function kelvinToRGB(kelvin) {
  const t = Math.min(40000, Math.max(1000, kelvin)) / 100

  let r, g, b

  // Red
  if (t <= 66) r = 255
  else r = clamp255(329.698727446 * Math.pow(t - 60, -0.1332047592))

  // Green
  if (t <= 66) g = clamp255(99.4708025861 * Math.log(t) - 161.1195681661)
  else g = clamp255(288.1221695283 * Math.pow(t - 60, -0.0755148492))

  // Blue
  if (t >= 66) b = 255
  else if (t <= 19) b = 0
  else b = clamp255(138.5177312231 * Math.log(t - 10) - 305.0447927307)

  return { r: r / 255, g: g / 255, b: b / 255 }
}

/** Kelvin → `#rrggbb` string (usable as a three.js/CSS color). */
export function kelvinToHex(kelvin) {
  const { r, g, b } = kelvinToRGB(kelvin)
  const h = (x) => Math.round(x * 255).toString(16).padStart(2, '0')
  return `#${h(r)}${h(g)}${h(b)}`
}
